import { SOURCE_CLIENT_LABELS } from "./colors";
import type { Citation } from "../types";

export interface CitationGroup<T> {
  label: string;
  items: T[];
}

function resolveLabel(citation: Citation): string {
  const dataSource = citation.metadata?.dataSource;
  if (dataSource && typeof dataSource === "string") {
    return dataSource;
  }
  const client = citation.client;
  if (client && typeof client === "string") {
    return SOURCE_CLIENT_LABELS[client] ?? client;
  }
  return "Other";
}

export function groupCitationsByDataSource(citations: Citation[]): CitationGroup<Citation>[] {
  if (citations.length === 0) {
    return [];
  }
  const map = new Map<string, Citation[]>();
  for (const citation of citations) {
    const label = resolveLabel(citation);
    const bucket = map.get(label);
    if (bucket) {
      bucket.push(citation);
    } else {
      map.set(label, [citation]);
    }
  }
  const out: CitationGroup<Citation>[] = [];
  map.forEach((items, label) => {
    out.push({ label, items });
  });
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

export function groupItemsByDataSource<
  T extends { citation?: Citation; envelope?: Citation; label?: string },
>(
  items: T[]
): CitationGroup<T>[] {
  if (items.length === 0) {
    return [];
  }
  const map = new Map<string, T[]>();
  for (const item of items) {
    const label =
      item.label ||
      (item.envelope
        ? resolveLabel(item.envelope)
        : item.citation
          ? resolveLabel(item.citation)
          : "Other");
    const bucket = map.get(label);
    if (bucket) {
      bucket.push(item);
    } else {
      map.set(label, [item]);
    }
  }
  const out: CitationGroup<T>[] = [];
  map.forEach((bucketItems, label) => {
    out.push({ label, items: bucketItems });
  });
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}
